/**
 * Sprint UX-BR — English (US) dictionary placeholder.
 *
 * Currently UNUSED — the system runs in pt-BR by default. This file
 * preserves the structure so a future sprint can fill the strings
 * without changing the i18n core.
 *
 * Strategy: keep keys identical to ptBR.js. Any missing key falls
 * back to pt-BR (see i18n/index.js → tInLocale).
 */

export const enUS = {
  // Minimal seed — most keys fall back to pt-BR for now.
  // This is intentional: the project is Brazilian-focused. When a real
  // English audience is needed, fill this dictionary by mirroring
  // ptBR.js key-by-key.

  common: {
    apply: "Apply",
    cancel: "Cancel",
    close: "Close",
    save: "Save",
    delete: "Delete",
    edit: "Edit",
    create: "Create",
    add: "Add",
    confirm: "Confirm",
    retry: "Retry",
    reload: "Reload",
    refresh: "Refresh",
    loading: "loading...",
    error: "Error",
    success: "Success",
    yes: "Yes",
    no: "No",
    none: "None",
    search: "Search",
    filter: "Filter",
    clear: "Clear",
    total: "Total",
    of: "of",
    showingOf: "Showing {n} of {total}",
  },

  status: {
    online: "Online",
    offline: "Offline",
    pending: "Pending",
    synced: "Synced",
    outdated: "Outdated",
    unknown: "Unknown",
    ok: "OK",
    fail: "Failed",
    busy: "Busy",
    skipped: "Skipped",
  },

  device: {
    fwVersion: "Firmware Version",
    lastHeartbeat: "Last Heartbeat",
    onlineOnly: "Online Only",
    offlineOnly: "Offline Only",
  },

  template: {
    apply: "Apply Template",
    applyAll: "🚀 Apply All",
    applyFiltered: "🎯 Apply Filtered",
    previewDiff: "🔬 Preview Diff",
    riskSummary: "Risk Summary",
    withoutSnapshot: "Without Snapshot",
    noChanges: "No Changes",
  },

  schedule: {
    title: "Schedule",
    history: "Run History",
    noEvents: "No events recorded.",
  },

  templatesPage: {
    title: "📋 Config Templates",
    newTemplate: "+ Create Template",
    btnApply: "🚀 Apply",
    titleApply: "Apply",
    titleHistory: "History",
    titleEdit: "Edit",
    titleDuplicate: "Duplicate",
    titleDelete: "Delete",
    enabledOn: "● ENABLED",
    enabledOff: "○ DISABLED",
    colName: "Name",
    colScope: "Scope",
    colTypes: "Types",
    colUpdated: "Updated",
    colStatus: "Status",
    colActions: "Actions",
    generalHistory: "📜 General History",
    visibilityGlobal: "Global",
    visibilityPrivate: "Private",
    visibilityMixed: "Mixed",
  },

  templateBuilder: {
    titleEdit: "✏ Edit Template",
    titleNew: "📄 New Template",
    save: "Save",
    create: "Create",
    saving: "Saving...",
    cancel: "Cancel",
    name: "Name",
    enabled: "Enabled",
    addEntry: "+ Add entry",
    removeEntry: "Remove entry",
    pulseReboot: "Pulse reboot",
    onOffFixed: "Fixed ON/OFF",
  },

  runHistory: {
    titleTemplate: "📜 Template Run History",
    titleGeneral: "📜 Run History",
    empty: "No runs yet.",
    targets: "Targets",
    created: "Commands",
    skipped: "Skipped",
    createdAt: "Created At",
    createdBy: "Created By",
    cancelRun: "🛑 Cancel Run",
    cancelling: "Canceling...",
    risks: "Risks",
    status: {
      completed: "Completed",
      partial: "Partial",
      failed: "Failed",
      dry_run: "Dry-run",
      canceled: "Canceled",
      pending: "Pending",
    },
  },

  devicesList: {
    searchPlaceholder: "Search: device_id, IP, firmware, customer code…",
    all: "All",
    online: "Online",
    degraded: "Degraded",
    offline: "Offline",
    colStatus: "Status",
    colCustomer: "Customer",
    colFw: "Firmware",
    colIp: "IP",
    colRelay: "Relay",
    colRssi: "RSSI",
    colUptime: "Uptime",
    colHeap: "Free Heap",
    colRebootReason: "Last Reboot",
    colLastSeen: "Last Seen",
    btnDetails: "details",
  },

  deviceDetail: {
    back: "← back",
    relayReboot: "Restart Relay",
    espReboot: "Restart ESP32",
    summary: "Summary",
    customer: "Customer",
    firmware: "Firmware",
    ipLocal: "Local IP",
    relay: "Relay",
    uptime: "Uptime",
    createdAt: "Created At",
    lastSeen: "Last Seen",
    // S7.5C-B — Fleet Health card
    health: {
      title: "Device Health",
      bootCount: "Boots",
      rebootReason: "Last reboot",
      heapFree: "Free heap",
      heapMin: "Min heap",
      rssiCurrent: "Current RSSI",
      rssiMin: "Min RSSI",
      wifiUptime: "Wi-Fi uptime",
      wifiDisconnects: "Wi-Fi disconnects",
      lastOtaVersion: "Last OTA",
      lastOtaConfirm: "OTA confirmed",
      otaEventsTitle: "Recent OTA events",
      otaEventsEmpty: "No OTA events recorded.",
      otaEventsLoading: "Loading OTA events…",
      otaEventsError: "Failed to load OTA events: {msg}",
    },
    // S7.6-A — Device Inventory Card.
    inventory: {
      title: "Device Inventory",
      serial: "Serial / QR",
      mac: "MAC",
      customer: "Customer",
      installedAt: "Installed on",
      lastHb: "Last HB",
      firmware: "Firmware",
    },
  },

  firmwareReleases: {
    title: "Firmware Releases",
    newRelease: "New Release",
    version: "Version",
    binFile: "Bin File",
    notes: "Notes (optional)",
    upload: "Upload",
    uploading: "Uploading...",
    releases: "Releases",
    showDeprecated: "Show deprecated",
    colVersion: "Version",
    colStatus: "Status",
    colSize: "Size",
    colCreated: "Created",
    colNotes: "Notes",
    statusDeprecated: "DEPRECATED",
    statusActive: "ACTIVE",
    btnRollout: "⇪ rollout",
    btnUndeprecate: "↩ undeprecate",
    btnDeprecate: "ⓧ deprecate",
    rolloutProgress: "Progress",
    rolloutFailures: "Failures",
    rolloutDone: "Done",
    cancelRollout: "Cancel rollout",
  },

  // S7.6-A — aging and heap badge labels (tooltips).
  offlineAging: {
    online: "online",
    warn: "Offline for more than 10 minutes",
    alert: "Offline for more than 1 hour",
    stale: "Offline for more than 24 hours",
  },
  heapAlert: {
    ok: "Healthy heap (≥80 KB free)",
    warn: "Low heap (50–80 KB free)",
    crit: "Critical heap (<50 KB free)",
  },
};

export default enUS;
